
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useAuth } from '@/contexts/auth-context';
import { useSidebarMobileToggle } from '@/components/side-nav';
import { useTranslations } from '@/i18n/use-translations';
import { useLocale } from '@/i18n/provider';
import { locales } from '@/i18n/config';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Bell,
  Check,
  ChevronDown,
  Menu,
  Plus,
  Search,
  LogOut,
  Settings,
  CreditCard,
  UserCircle,
  Sun,
  Moon,
} from 'lucide-react';
import { PlanBadge } from '@/components/plan-badge';
import { getInitials, shouldShowNav } from '@/lib/formatters';
import { useOrg } from '@/hooks/use-org';
import { api, getApiUrl } from '@/lib/api';
import { useFlag } from '@/contexts/flags-context';
import { applyAgentBetaTheme, readAgentBetaThemeMode, saveAgentBetaThemeMode, type AgentBetaThemeMode } from '@/lib/agent-beta-theme';

export function TopNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isAuthenticated, logout, tenant, admin } = useAuth();
  const { currentOrg, orgs, switchOrg } = useOrg();
  const toggleMobile = useSidebarMobileToggle();
  const t = useTranslations('topNav');
  const tNav = useTranslations('nav');
  const tLocale = useTranslations('locale');
  const { locale, setLocale } = useLocale();
  const betaTheme = useFlag('agent-beta-theme');
  const [themeMode, setThemeMode] = useState<AgentBetaThemeMode>(readAgentBetaThemeMode);
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const [orgTenants, setOrgTenants] = useState<any[]>([]);

  useEffect(() => {
    if (!currentOrg) return;
    api.tenants.list({ org_id: currentOrg.id }).then(setOrgTenants).catch(() => {});
  }, [currentOrg?.id]);

  const handleSelectTenant = async (appTenant: { id: string; name: string }) => {
    if (!token) return;
    try {
      const res = await fetch(`${getApiUrl()}/auth/tenants/${appTenant.id}/select`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        localStorage.setItem('token', data.data.token);
        localStorage.setItem('tenant', JSON.stringify(data.data.tenant));
        localStorage.setItem('schemaName', data.data.schemaName);
        window.location.assign('/dashboard');
      }
    } catch {
      // ignore
    }
  };

  const toggleTheme = () => {
    const next: AgentBetaThemeMode = themeMode === 'dark' ? 'light' : 'dark';
    setThemeMode(next);
    saveAgentBetaThemeMode(next);
    if (betaTheme) applyAgentBetaTheme(true, next);
  };

  if (!shouldShowNav(pathname, isAuthenticated)) return null;

  // On tenants page, show minimal nav
  if (pathname === '/tenants') {
    return (
      <header className="sticky top-0 z-30 flex h-12 items-center border-b border-border bg-background/80 backdrop-blur-sm px-4">
        <div className="flex items-center gap-2">
          <span className="text-base font-semibold text-violet">fyso</span>
        </div>
        <div className="flex-1" />
        <Button variant="ghost" size="sm" onClick={logout} className="text-muted-foreground">
          <LogOut className="mr-2 h-4 w-4" />
          {t('logout')}
        </Button>
      </header>
    );
  }

  return (
    <header className="sticky top-0 z-30 flex h-12 items-center gap-3 border-b border-border bg-background/80 backdrop-blur-sm px-4">
      {/* Mobile hamburger */}
      <button
        onClick={toggleMobile}
        className="lg:hidden rounded-md p-1.5 text-muted-foreground hover:bg-accent"
        aria-label={t('toggleMenu')}
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Logo / App name */}
      <div className="flex items-center gap-2">
        {tenant?.logoUrl ? (
          <img src={tenant.logoUrl} alt={tenant.appName || tenant.name} className="h-6 w-6 rounded object-contain" />
        ) : null}
        <span className="text-base font-semibold text-violet">
          {tenant?.appName || 'fyso'}
        </span>
      </div>

      {/* Org + Tenant switcher */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="org-switcher-trigger" className="flex items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-accent transition-colors">
            <div className="flex flex-col items-start leading-tight">
              {currentOrg && (
                <span data-testid="org-switcher-org-name" className="text-[10px] text-muted-foreground">
                  {currentOrg.is_personal && currentOrg.my_role !== 'owner' && currentOrg.owner_name
                    ? `Personal (${currentOrg.owner_name})`
                    : currentOrg.name}
                </span>
              )}
              <div className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 rounded-full bg-success shrink-0" />
                <span data-testid="org-switcher-tenant-name" className="font-medium truncate max-w-[100px] sm:max-w-[160px]">
                  {tenant?.name || t('noApp')}
                </span>
              </div>
            </div>
            <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent data-testid="org-switcher-dropdown" align="start" className="w-64">
          {/* Org section — always show when there are orgs */}
          {orgs.length > 0 && (
            <>
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
                Organizaciones
              </DropdownMenuLabel>
              {orgs.map((org) => {
                const orgLabel = org.is_personal && org.my_role !== 'owner' && org.owner_name
                  ? `Personal (${org.owner_name})`
                  : org.name;
                const isOwner = org.my_role === 'owner';
                const isSelected = org.id === currentOrg?.id;
                return (
                  <DropdownMenuItem
                    key={org.id}
                    data-testid={`org-switcher-org-${org.id}`}
                    onClick={() => switchOrg(org.id)}
                    className={`flex items-center gap-2 ${isSelected ? 'bg-accent' : ''}`}
                  >
                    <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold shrink-0">
                      {orgLabel.charAt(0).toUpperCase()}
                    </div>
                    <span className="flex-1 truncate">{orgLabel}</span>
                    <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                      isOwner
                        ? 'bg-amber-500/15 text-amber-500'
                        : 'bg-blue-500/15 text-blue-400'
                    }`}>
                      {isOwner ? 'Propietario' : 'Miembro'}
                    </span>
                    {isSelected && <Check className="h-3.5 w-3.5 text-violet shrink-0" />}
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuSeparator />
            </>
          )}

          {/* Tenants in selected org */}
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
            {currentOrg ? `Apps en ${currentOrg.name}` : 'Apps'}
          </DropdownMenuLabel>
          {orgTenants.length > 0 ? (
            orgTenants.map((app) => (
              <DropdownMenuItem
                key={app.id}
                data-testid={`org-switcher-tenant-${app.id}`}
                onClick={() => handleSelectTenant(app)}
                className="flex items-center gap-2"
              >
                <span className="inline-block h-2 w-2 rounded-full bg-success shrink-0" />
                <span className="flex-1 truncate">{app.name}</span>
                {app.id === tenant?.id && <Check className="h-3.5 w-3.5 text-violet shrink-0" />}
              </DropdownMenuItem>
            ))
          ) : (
            <DropdownMenuItem disabled className="text-xs text-muted-foreground">
              Sin apps en esta org
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          {/* Actions */}
          <DropdownMenuItem data-testid="org-switcher-switch-app" onClick={() => navigate('/tenants')}>
            <Plus className="mr-2 h-4 w-4" />
            {t('switchApp')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Search trigger (Cmd+K) */}
      <button
        onClick={() => {
          document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
        }}
        className="hidden sm:flex w-64 items-center gap-2 rounded-md border border-border/50 bg-muted/50 px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted transition-colors duration-150"
      >
        <Search className="h-3.5 w-3.5" />
        <span>{t('search')}</span>
        <kbd className="ml-4 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">
          {'\u2318'}K
        </kbd>
      </button>

      {/* Plan badge */}
      <PlanBadge plan={currentOrg?.plan ?? admin?.plan} />

      {/* Notifications */}
      {betaTheme && (
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={themeMode === 'dark' ? 'Usar modo claro' : 'Usar modo oscuro'}
          title={themeMode === 'dark' ? 'Modo claro' : 'Modo oscuro'}
          className="rounded-md p-1.5 text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors duration-150"
        >
          {themeMode === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
      )}
      <button className="relative rounded-md p-1.5 text-muted-foreground hover:bg-muted/50 transition-colors duration-150">
        <Bell className="h-4 w-4" />
        <span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-violet" />
      </button>

      {/* User avatar dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="rounded-full">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-violet text-white text-xs">
                {getInitials(admin?.name)}
              </AvatarFallback>
            </Avatar>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <div className="px-2 py-1.5">
            <p className="text-sm font-medium">{admin?.name}</p>
            <p className="text-xs text-muted-foreground">{admin?.email}</p>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/settings/profile')}>
            <UserCircle className="mr-2 h-4 w-4" />
            {tNav('profile')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/settings/general')}>
            <Settings className="mr-2 h-4 w-4" />
            {tNav('settings')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/org/billing')}>
            <CreditCard className="mr-2 h-4 w-4" />
            {tNav('billing')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <div className="px-2 py-1.5">
            <p className="text-xs text-muted-foreground mb-1">{tLocale('switchLocale')}</p>
            <div className="flex gap-1">
              {locales.map((loc) => (
                <button
                  key={loc}
                  onClick={() => setLocale(loc)}
                  className={`rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                    locale === loc
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                  }`}
                >
                  {loc.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout}>
            <LogOut className="mr-2 h-4 w-4" />
            {t('closeSession')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
